// src/components/ProtectedRoute.jsx
import React, { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import LoadingSpinner from "../common/LoadingSpinner";
import { DEACTIVATED_ACCOUNT, VET_ROLE_ID } from "../../context/PermissionConstants";

const isVetAllowedPath = (pathname) => (
    pathname === '/' ||
    pathname === '/vet-board' ||
    pathname === '/workflow' ||
    pathname === '/appointments' ||
    pathname === '/treatments' ||
    pathname === '/overview' ||
    pathname.startsWith('/overview/') ||
    pathname === '/programs/breeding' ||
    pathname.startsWith('/programs/breeding/') ||
    pathname === '/programs/rehab' ||
    pathname.startsWith('/programs/rehab/') ||
    pathname === '/myaccount'
);

function ProtectedRoute({ minRank, allowViewOnly = false, allowVet = false, superBypass = false, children }) {
    const {
        user,
        loading,
        isShareLinkUser,
        checkIsAllowedPath,   // ← FIXED
    } = useContext(AuthContext);

    const location = useLocation();

    if (loading) return <LoadingSpinner fullPage />;
    if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

    if (user.role_id === DEACTIVATED_ACCOUNT) {
        return <Navigate to="/unauthorized" replace />;
    }

    // Share-link user path restriction
    if (isShareLinkUser && !checkIsAllowedPath(location.pathname)) {
        const safePath = user.shareLinkData?.allowedPath || "/";
        return <Navigate to={safePath} replace />;
    }

    // Accountant (121) is a bypass, not a rank, and it is opt-in per route
    // (MGMT-0044). It used to clear every `allowViewOnly` page, which is most
    // of the app -- far wider than Sidebar.js has ever advertised, and wider
    // than the backend now allows. Flagged routes bypass; everything else falls
    // through to the ordinary rank check below, so the accountant is only ever
    // shown a page whose API will actually answer it.
    if (user.role_id === 121 && superBypass) return children;

    if (user.role_id === VET_ROLE_ID) {
        // Explicit route allowlist is evaluated before rank checks.  A Vet is
        // not a low-rank staff account; these are the only app surfaces it can use.
        return (allowVet || isVetAllowedPath(location.pathname))
            ? children
            : <Navigate to="/unauthorized" replace />;
    }

    if (minRank !== undefined && user.role_id > minRank) {
        return <Navigate to="/unauthorized" replace />;
    }

    return <>{children}</>;
}


export default ProtectedRoute;
